/* eslint-disable no-console */
import Axios from 'src/services/axios';
import { AxiosError, AxiosResponse } from 'axios';
import { toast } from 'react-toastify';
import ServiceContainer from 'src/ServiceContainer';

enum LoggingSeverity {
  SEVERITY_TRACE = 'TRACE',
  SEVERITY_INFO = 'INFO',
  SEVERITY_DEBUG = 'DEBUG',
  SEVERITY_WARN = 'WARN',
  SEVERITY_ERROR = 'ERROR',
}

interface LoggingPayload {
  severity: LoggingSeverity;
  message: string;
  payload: {
    stack: string | undefined;
  };
  userId?: string;
  sessionId?: string;
}

const isAxiosError = (error: object): error is AxiosError => {
  return 'isAxiosError' in error;
};

export const errorToLoggingPayload = (error?: unknown): string => {
  const noErrorInformationMessage = 'No additional error information';
  const unknownErrorInformationMessage = 'Unknown Error Occured';

  if (error === undefined || error === null) {
    return noErrorInformationMessage;
  }
  // "string" | "number" | "bigint" | "boolean" | "symbol" | "undefined" | "object" | "function"
  switch (typeof error) {
    case 'function':
    case 'undefined':
      return noErrorInformationMessage;
    case 'bigint':
    case 'number':
    case 'boolean':
    case 'symbol':
      return error.toString();
    case 'string':
      return error;
    case 'object':
      const errorObj = error;
      if (errorObj instanceof Error) {
        return errorObj.stack ?? errorObj.message;
      }
      if (isAxiosError(errorObj)) {
        const serverResponse = errorObj.response ? errorToLoggingPayload(errorObj.response) : null;
        const serverCode = errorObj.code ? errorObj.code : 'No Code Returned';
        return serverResponse ? `HTTP Error: ${serverCode} Response: ${serverResponse}` : `HTTP Error: ${serverCode}`;
      }
      return unknownErrorInformationMessage;
    default:
      return unknownErrorInformationMessage;
  }
};

export function logError(message: string, error?: unknown) {
  ServiceContainer.loggingService.error(message, errorToLoggingPayload(error));
}
export function logWarn(message: string, error?: unknown) {
  ServiceContainer.loggingService.warn(message, errorToLoggingPayload(error));
}

export function toastAndLog(message: string, error?: unknown) {
  if (error) {
    toast.error(message);
    logError(message, error);
  } else {
    toast.info(message);
    ServiceContainer.loggingService.info(message);
  }
}

/**
 * Truncate stack message to prevent loss of log information
 * due to sending message body that exceeds the backend's max size.
 */
function formatStack(rawStack: string | undefined): string | undefined {
  const stackLength = rawStack?.length ?? 0;
  return stackLength > 10_000 ? rawStack?.slice(0, 10_000) : rawStack;
}

class LoggingService {
  // TODO: pull the userid from the request and pass it in as a constructor param
  // to be send to the server

  private getLogOutput(message: string, stack?: string) {
    return stack ? `${message}\n${stack}` : `${message}`;
  }

  public trace(message: string, rawStack?: string, skipLocalLog = false) {
    if (!skipLocalLog) {
      const output = this.getLogOutput(message, rawStack);
      console.trace(output);
    }

    return this.postLogs({
      severity: LoggingSeverity.SEVERITY_TRACE,
      message,
      payload: {
        stack: formatStack(rawStack),
      },
    });
  }
  public info(message: string, rawStack?: string, skipLocalLog = false) {
    if (!skipLocalLog) {
      const output = this.getLogOutput(message, rawStack);
      console.info(output);
    }

    return this.postLogs({
      severity: LoggingSeverity.SEVERITY_INFO,
      message,
      payload: {
        stack: formatStack(rawStack),
      },
    });
  }
  public debug(message: string, rawStack?: string, skipLocalLog = false) {
    if (!skipLocalLog) {
      const output = this.getLogOutput(message, rawStack);
      console.debug(output);
    }

    return this.postLogs({
      severity: LoggingSeverity.SEVERITY_DEBUG,
      message,
      payload: {
        stack: formatStack(rawStack),
      },
    });
  }
  public warn(message: string, rawStack?: string, skipLocalLog = false) {
    if (!skipLocalLog) {
      const output = this.getLogOutput(message, rawStack);
      console.warn(output);
    }

    return this.postLogs({
      severity: LoggingSeverity.SEVERITY_WARN,
      message,
      payload: {
        stack: formatStack(rawStack),
      },
    });
  }
  public error(message: string, rawStack?: string, skipLocalLog = false) {
    if (!skipLocalLog) {
      const output = this.getLogOutput(message, rawStack);
      console.error(output);
    }

    return this.postLogs({
      severity: LoggingSeverity.SEVERITY_ERROR,
      message,
      payload: {
        stack: formatStack(rawStack),
      },
    });
  }

  private postLogs(log: LoggingPayload) {
    // remote server takes a list of errors
    // TODO: batch errors and send them in one request
    return Axios.post<AxiosResponse>(`/api/log`, [log]).then((response) => response.data);
  }
}

export default LoggingService;
